import { useState, useEffect } from 'react';
import { useStoreContext } from '../../context/StoreContext';
import { MoveDown, PartyPopper, Mail } from 'lucide-react';

export default function SpinToWinPopup() {
  const { store } = useStoreContext();
  const isActive = store?.active_apps?.includes('spin-to-win');
  const settings = store?.settings?.apps?.spin_to_win || {};
  const discountCode = settings.discount_code || 'LUCKY15';
  const discountText = settings.discount_text || '15% OFF';

  const [show, setShow] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [rotation, setRotation] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isActive) return;
    
    // Only show once per device/browser using localStorage
    const hasSeen = localStorage.getItem(`spinToWinShown_${store?.id}`);
    if (hasSeen === 'true') return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Exit intent detection
      if (e.clientY <= 0) {
        setShow(true);
        localStorage.setItem(`spinToWinShown_${store?.id}`, 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [isActive, store?.id]);

  const handleSpin = () => {
    if (!email || spinning) return;
    setSpinning(true);
    
    // Random rotation between 5 and 10 full circles
    const spins = 5 + Math.random() * 5; 
    const finalDegree = 360 * spins + Math.floor(Math.random() * 360);
    setRotation(finalDegree);

    setTimeout(() => {
      setSpinning(false);
      setResult(discountText);
    }, 4500); // 4.5 seconds spin duration
  };

  const copyCode = () => {
    navigator.clipboard.writeText(discountCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isActive || !show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes popIn { 
          0% { transform: scale(0.9); opacity: 0; } 
          100% { transform: scale(1); opacity: 1; } 
        }
        @keyframes pulseGlow {
          0% { box-shadow: 0 0 0 0 rgba(253, 60, 2, 0.4); }
          70% { box-shadow: 0 0 0 20px rgba(253, 60, 2, 0); }
          100% { box-shadow: 0 0 0 0 rgba(253, 60, 2, 0); }
        }
        .spin-wheel {
          background: conic-gradient(
            #fd3c02 0deg 45deg, #fef08a 45deg 90deg, 
            #fd3c02 90deg 135deg, #fef08a 135deg 180deg, 
            #fd3c02 180deg 225deg, #fef08a 225deg 270deg, 
            #fd3c02 270deg 315deg, #fef08a 315deg 360deg
          );
        }
      `}</style>
      
      <div style={{
        background: '#ffffff', borderRadius: '24px', maxWidth: '850px', width: '100%',
        display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 1fr', overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
        animation: 'popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
      }}>
        {/* Left: The Wheel */}
        <div style={{ 
          background: '#f8fafc', padding: '40px', 
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          borderRight: '1px solid #e2e8f0'
        }}>
          <div style={{ 
            position: 'absolute', top: '15px', fontSize: '32px', zIndex: 10,
            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))', color: '#0f172a'
          }}><MoveDown size={32} /></div>
          <div style={{
            position: 'relative', width: '280px', height: '280px', 
            borderRadius: '50%', border: '8px solid #0f172a', 
            boxShadow: '0 10px 25px rgba(0,0,0,0.1), inset 0 0 20px rgba(0,0,0,0.2)'
          }}>
            <div 
              className="spin-wheel"
              style={{ 
                width: '100%', height: '100%', borderRadius: '50%',
                transition: 'transform 4.5s cubic-bezier(0.12, 0.99, 0.2, 0.99)',
                transform: 'rotate(' + rotation + 'deg)'
              }}
            />
            {/* Center dot */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '40px', height: '40px', background: '#0f172a', borderRadius: '50%',
              border: '4px solid #fff', zIndex: 5
            }}></div>
          </div>
        </div>

        {/* Right: Content */}
        <div style={{ padding: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
          <button 
            onClick={() => setShow(false)} 
            style={{ 
              position: 'absolute', top: '24px', right: '24px', 
              background: '#f1f5f9', border: 'none', 
              width: '32px', height: '32px', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' 
            }}
            onMouseOver={e => e.currentTarget.style.background = '#e2e8f0'}
            onMouseOut={e => e.currentTarget.style.background = '#f1f5f9'}
          >
            &times;
          </button>
          
          {result ? (
            <div style={{ textAlign: 'center', animation: 'popIn 0.5s ease' }}>
              <div style={{ fontSize: '56px', marginBottom: '8px', color: '#fd3c02' }}><PartyPopper size={56} /></div>
              <h2 style={{ fontSize: '32px', fontWeight: 800, margin: '0 0 8px', color: '#0f172a' }}>You Won!</h2>
              <div style={{ 
                fontSize: '28px', fontWeight: 900, color: '#fd3c02', 
                background: '#fff1f2', padding: '16px 24px', borderRadius: '16px', 
                display: 'inline-block', marginBottom: '24px', border: '2px dashed #fecdd3' 
              }}>
                {result}
              </div>
              <p style={{ color: '#475569', marginBottom: '16px', fontSize: '16px' }}>
                Use this code at checkout to claim your prize!
              </p>
              
              <div style={{ 
                display: 'flex', background: '#f8fafc', border: '1px solid #e2e8f0', 
                borderRadius: '12px', padding: '4px', marginBottom: '24px' 
              }}>
                <div style={{ flex: 1, padding: '12px 16px', fontWeight: 700, fontSize: '18px', letterSpacing: '1px' }}>
                  {discountCode}
                </div>
                <button 
                  onClick={copyCode}
                  style={{ 
                    background: copied ? '#10b981' : '#0f172a', color: '#fff', 
                    border: 'none', borderRadius: '8px', padding: '0 20px', 
                    fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' 
                  }}
                >
                  {copied ? 'Copied! ✓' : 'Copy'}
                </button>
              </div>
              
              <button 
                onClick={() => setShow(false)}
                style={{ 
                  width: '100%', height: '52px', background: '#fd3c02', color: '#fff', 
                  border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 700, 
                  cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(253, 60, 2, 0.2)' 
                }}
              >
                Continue Shopping →
              </button>
            </div>
          ) : (
            <div style={{ animation: 'fadeIn 0.4s ease' }}>
              <h2 style={{ fontSize: '36px', fontWeight: 900, margin: '0 0 12px', lineHeight: 1.1, color: '#0f172a' }}>
                Wait! <br/>
                <span style={{ color: '#fd3c02' }}>Don't leave empty handed.</span>
              </h2>
              <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '32px', lineHeight: 1.5 }}>
                Spin the wheel for a guaranteed chance to win a special discount on your order today!
              </p>
              
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <input 
                  type="email" 
                  placeholder="Enter your email to spin..." 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ 
                    width: '100%', height: '52px', padding: '0 16px 0 44px', 
                    borderRadius: '12px', border: '2px solid #e2e8f0', 
                    fontSize: '16px', boxSizing: 'border-box',
                    transition: 'border-color 0.2s', background: '#f8fafc'
                  }}
                  onFocus={e => e.target.style.borderColor = '#fd3c02'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
                <span style={{ position: 'absolute', left: '16px', top: '16px', fontSize: '18px', color: '#94a3b8' }}><Mail size={18} /></span>
              </div>
              
              <button 
                onClick={handleSpin}
                disabled={spinning || !email}
                style={{ 
                  width: '100%', height: '56px', background: '#fd3c02', color: '#fff', 
                  border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: 700, 
                  cursor: (spinning || !email) ? 'not-allowed' : 'pointer', 
                  opacity: (spinning || !email) ? 0.7 : 1,
                  boxShadow: spinning || !email ? 'none' : '0 10px 15px -3px rgba(253, 60, 2, 0.3)',
                  animation: (!spinning && email) ? 'pulseGlow 2s infinite' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {spinning ? 'Spinning the wheel...' : 'Try My Luck 🎡'}
              </button>
              
              <button 
                onClick={() => setShow(false)}
                style={{ 
                  width: '100%', background: 'none', border: 'none', color: '#94a3b8', 
                  fontSize: '14px', marginTop: '20px', cursor: 'pointer',
                  textDecoration: 'underline', textUnderlineOffset: '4px'
                }}
              >
                No thanks, I'll pay full price.
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
